import { timeLabel } from "../dashboard/local-day.js";
import type { RecordEntry } from "../records/records-api.js";
import { recordHeadline } from "../records/records-view.js";
import type { TaskOccurrence } from "../tasks/tasks-api.js";
import type { FollowUp } from "./followups-api.js";

export interface AttentionLookups {
  // Equipment, supplier and staff names by id.
  names: ReadonlyMap<string, string>;
  occurrences: ReadonlyMap<
    string,
    Pick<TaskOccurrence, "due_at" | "task_definition_id" | "title">
  >;
  records: ReadonlyMap<string, RecordEntry>;
  typeTitles: ReadonlyMap<string, string>;
}

export interface AttentionRow {
  detail: string[];
  response: string | null;
  severity: "critical" | "major" | null;
  title: string;
}

const kindLabels: Record<FollowUp["trigger_kind"], string | null> = {
  incident: "Problem reported",
  manual: null,
  missed_task: null,
  rejected_delivery: "Rejected delivery",
  rule_fail: "Failed",
  rule_warn: "Outside the usual range",
};

const fallbackTitles: Record<FollowUp["trigger_kind"], string> = {
  incident: "Problem reported",
  manual: "Follow-up",
  missed_task: "Missed check",
  rejected_delivery: "Rejected delivery",
  rule_fail: "Failed check",
  rule_warn: "Outside the usual range",
};

function localDate(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(
    new Date(value),
  );
}

// "4:16 pm" today, "Yesterday 4:16 pm", otherwise "20 Oct, 4:16 pm".
export function whenLabel(value: string, timezone: string, now = new Date()) {
  const day = localDate(value, timezone);
  const today = localDate(now.toISOString(), timezone);
  const yesterday = localDate(
    new Date(now.getTime() - 86_400_000).toISOString(),
    timezone,
  );
  const time = timeLabel(value, timezone);
  if (day === today) return time;
  if (day === yesterday) return `Yesterday ${time}`;
  const date = new Intl.DateTimeFormat("en-NZ", {
    day: "numeric",
    month: "short",
    timeZone: timezone,
  }).format(new Date(value));
  return `${date}, ${time}`;
}

// "Today", "Yesterday", otherwise "20 Oct".
export function dayLabel(value: string, timezone: string, now = new Date()) {
  const day = localDate(value, timezone);
  if (day === localDate(now.toISOString(), timezone)) return "Today";
  if (
    day ===
    localDate(new Date(now.getTime() - 86_400_000).toISOString(), timezone)
  )
    return "Yesterday";
  return new Intl.DateTimeFormat("en-NZ", {
    day: "numeric",
    month: "short",
    timeZone: timezone,
  }).format(new Date(value));
}

export interface MissedCluster {
  detail: string;
  // When each item was due, in the same order as items.
  dues: string[];
  items: FollowUp[];
  key: string;
  severity: AttentionRow["severity"];
  title: string;
}

// The same check missed on several days becomes one row, newest first inside.
export function clusterMissed(
  items: readonly FollowUp[],
  lookups: AttentionLookups,
  timezone: string,
  now = new Date(),
): MissedCluster[] {
  const clusters = new Map<
    string,
    { dues: string[]; items: FollowUp[]; title: string }
  >();
  for (const item of items) {
    const occurrence = item.task_occurrence_id
      ? lookups.occurrences.get(item.task_occurrence_id)
      : undefined;
    const key = occurrence?.task_definition_id ?? occurrence?.title ?? item.id;
    const entry = clusters.get(key) ?? {
      dues: [],
      items: [],
      title: occurrence?.title ?? fallbackTitles.missed_task,
    };
    entry.items.push(item);
    entry.dues.push(occurrence?.due_at ?? item.created_at);
    clusters.set(key, entry);
  }
  const rank = { critical: 0, major: 1, minor: 2 } as const;
  return [...clusters.entries()]
    .map(([key, entry]) => {
      const order = entry.items
        .map((item, index) => ({ due: entry.dues[index]!, item }))
        .sort((a, b) => b.due.localeCompare(a.due));
      const newest = order[0]!.due;
      const oldest = order[order.length - 1]!.due;
      const worst = entry.items.reduce(
        (current, item) =>
          rank[item.severity] < rank[current] ? item.severity : current,
        "minor" as FollowUp["severity"],
      );
      return {
        detail:
          order.length === 1
            ? whenLabel(newest, timezone, now)
            : `Missed ${order.length} times · ${dayLabel(oldest, timezone, now)} to ${rangeEnd(dayLabel(newest, timezone, now))}`,
        dues: order.map((entry) => entry.due),
        items: order.map((entry) => entry.item),
        key,
        newest,
        severity: worst === "minor" ? null : worst,
        title: entry.title,
      };
    })
    .sort(
      (a, b) =>
        rank[a.severity ?? "minor"] - rank[b.severity ?? "minor"] ||
        b.items.length - a.items.length ||
        b.newest.localeCompare(a.newest),
    )
    .map((cluster) => ({
      detail: cluster.detail,
      dues: cluster.dues,
      items: cluster.items,
      key: cluster.key,
      severity: cluster.severity,
      title: cluster.title,
    }));
}

function rangeEnd(label: string): string {
  return label === "Today" || label === "Yesterday"
    ? label.toLowerCase()
    : label;
}

export function describeFollowUp(
  item: FollowUp,
  lookups: AttentionLookups,
  timezone: string,
  now = new Date(),
): AttentionRow {
  const severity =
    item.severity === "critical" || item.severity === "major"
      ? item.severity
      : null;
  const response = item.staff_response?.trim() || null;

  if (item.trigger_kind === "missed_task") {
    const occurrence = item.task_occurrence_id
      ? lookups.occurrences.get(item.task_occurrence_id)
      : undefined;
    return {
      detail: [whenLabel(occurrence?.due_at ?? item.created_at, timezone, now)],
      response,
      severity,
      title: occurrence?.title ?? fallbackTitles.missed_task,
    };
  }

  const record = item.record_id ? lookups.records.get(item.record_id) : null;
  if (!record) {
    return {
      detail: [
        kindLabels[item.trigger_kind],
        whenLabel(item.created_at, timezone, now),
      ].filter((part): part is string => Boolean(part)),
      response,
      severity,
      title: fallbackTitles[item.trigger_kind],
    };
  }

  return {
    detail: [
      kindLabels[item.trigger_kind],
      whenLabel(record.occurred_at, timezone, now),
      lookups.names.get(record.performed_by_staff_id),
    ].filter((part): part is string => Boolean(part)),
    response,
    severity,
    title:
      recordHeadline(
        record,
        lookups.names,
        lookups.typeTitles.get(record.record_type_code),
      ) || fallbackTitles[item.trigger_kind],
  };
}
